// The simple Warmup page's words (owner, 2026-10-23: "600 settings", "I turned
// on the account number but further down there's no number"). On top, the three
// SES accounts' numbers; under them, one line per country with one switch; the
// rest is in "Részletek". What is said here is why a number is 0 — never a bare
// 0. Apart from the components so tests/admin runs it on Node.

import type { ContactSummary, Handover, WarmupAccount, WarmupPool } from "@/lib/api";
import type { Tone } from "@/components/emc/status";
import { count } from "@/components/emc/format";
import { KNOWN_COUNTRIES } from "@/pages/contacts/model";

/** The switch's name, everywhere on the page. */
export const SWITCH = "„Klaviyo is küld”";

/** Where a country stands, in one word: what decides whether we mail it. */
export type CountryStatus = "closed" | "waves" | "parallel" | "cut_over" | "free";

export interface CountryRow {
  country: string;
  /** The SES account it sends from, or null with no active sending account. */
  account: string | null;
  pool: string | null;
  status: CountryStatus;
  /** N while migrating. */
  wave: number | null;
  /** Who the split counted as mailable now, or null when no started account counts it. */
  mailable: number | null;
  /** Subscribed, present, not excluded — before the handover (the contacts summary). */
  sendable: number | null;
  /** The switch: on, and whether it can be moved at all. */
  parallel: boolean;
  switchable: boolean;
  /** The row the dialogs need; null when the country has no handover row. */
  handover: Handover | null;
}

export function statusOf(row: Handover | null | undefined): CountryStatus {
  if (!row) return "free";
  if (row.parallel || row.state === "parallel") return "parallel";
  if (row.state === "cut_over") return "cut_over";
  return row.wave === 0 ? "closed" : "waves";
}

const STATUS_WORDS: Record<CountryStatus, { word: string; tone: Tone; title: string }> = {
  closed: {
    word: "zárva",
    tone: "warn",
    title: "A Klaviyo küld nekik, mi senkinek (átadás alatt, N = 0).",
  },
  waves: {
    word: "hullámokban",
    tone: "running",
    title: "Átadás alatt: csak a Klaviyónak már átadott hullámoknak küldünk.",
  },
  parallel: {
    word: "párhuzamos",
    tone: "running",
    title: "A Klaviyo is küld nekik, és mi is: nincs átadás-szűrés.",
  },
  cut_over: {
    word: "átállva",
    tone: "good",
    title: "A Klaviyo már nem küld: minden feliratkozott a mienk.",
  },
  free: {
    word: "nincs korlát",
    tone: "muted",
    title: "Nincs átadás-sor: semmi nem korlátozza az országot.",
  },
};

/** The status word, its tone and its hover. N is said where it decides. */
export function statusWords(row: Pick<CountryRow, "status" | "wave">): {
  word: string;
  tone: Tone;
  title: string;
} {
  const look = STATUS_WORDS[row.status];
  if (row.status === "waves" && row.wave !== null) return { ...look, word: `hullámokban (N = ${row.wave})` };
  return look;
}

/** The twelve countries in the usual order, then any other the data names. */
export function countryRows(input: {
  handover: Handover[] | null;
  accounts: WarmupAccount[];
  pools: WarmupPool[];
  summary?: ContactSummary | null;
}): CountryRow[] {
  const byCountry = new Map((input.handover ?? []).map((row) => [row.country, row]));
  const poolOf = new Map<string, WarmupPool>();
  for (const pool of input.pools) for (const country of pool.countries) poolOf.set(country, pool);
  const counted = new Map<string, number>();
  for (const account of input.accounts) {
    for (const share of account.split) {
      for (const entry of share.countries) counted.set(entry.country, entry.mailable);
    }
  }
  const sendable = new Map((input.summary?.countries ?? []).map((row) => [row.country, row.sendable]));
  const extra = [...byCountry.keys(), ...poolOf.keys()].filter((code) => !KNOWN_COUNTRIES.includes(code));
  const codes = [...KNOWN_COUNTRIES, ...[...new Set(extra)].sort()];
  return codes.map((country) => {
    const handover = byCountry.get(country) ?? null;
    const pool = poolOf.get(country) ?? null;
    const status = statusOf(handover);
    return {
      country,
      account: pool ? pool.ses_account.toUpperCase() : null,
      pool: pool ? pool.code : null,
      status,
      wave: handover && status === "waves" ? handover.wave : null,
      mailable: counted.get(country) ?? null,
      sendable: sendable.get(country) ?? null,
      parallel: status === "parallel",
      switchable: status === "closed" || status === "waves" || status === "parallel",
      handover,
    };
  });
}

/** The mailable figure of a country row, in words: never a bare 0. */
export function mailableText(row: CountryRow): string {
  if (row.account === null) return "nincs aktív küldő-fiók";
  if (row.mailable === null) {
    return row.sendable === null
      ? "a fiók még nem indult"
      : `a fiók még nem indult · ${count(row.sendable)} feliratkozott`;
  }
  if (row.mailable > 0) return count(row.mailable);
  if (row.status === "closed") {
    return row.sendable ? `0 — zárva, ${count(row.sendable)} feliratkozott vár` : "0 — zárva";
  }
  if (row.status === "waves") return "0 — még senki sincs átadva";
  return "0 — nincs feliratkozott, ki nem zárt kontakt";
}

function listed(codes: string[]): string {
  return codes.join(", ");
}

function countriesOf(account: WarmupAccount): string[] {
  return account.split.flatMap((share) => share.countries.map((entry) => entry.country));
}

export interface Notice {
  tone: "warn" | "muted";
  text: string;
}

/** Why the account's number does not reach anybody, or which countries it
 * does not reach — one plain sentence, or null when the number is at work. */
export function accountNotice(account: WarmupAccount, rows: CountryRow[]): Notice | null {
  if (account.state === "cold") {
    return {
      tone: "muted",
      text: "Még nem indult: az „Indítás” gombbal, egy napi számmal indul, és onnan a kód osztja szét.",
    };
  }
  const stopped = account.pools.filter((pool) => pool.is_paused || pool.held_by_watch);
  if (account.pools.length > 0 && stopped.length === account.pools.length) {
    // The card's own banner says which pools stand and why.
    return null;
  }
  if (account.daily_limit <= 0) {
    return { tone: "warn", text: "A napi szám 0: a „Napi szám” gombbal állítható." };
  }
  const countries = countriesOf(account);
  const status = new Map(rows.map((row) => [row.country, row.status]));
  const closed = countries.filter((country) => status.get(country) === "closed");
  if (account.mailable <= 0) {
    if (countries.length === 0) {
      return {
        tone: "warn",
        text: "0 küldhető: egyik poolján sincs aktív küldő-fiók, így senkinek sem megy a számból.",
      };
    }
    if (closed.length === countries.length) {
      return {
        tone: "warn",
        text: `0 küldhető: egyik országa sincs megnyitva — kapcsold be a ${SWITCH}-et lent.`,
      };
    }
    const open = countries.filter((country) => !closed.includes(country));
    const tail = closed.length > 0 ? `; ${listed(closed)} zárva — kapcsold be a ${SWITCH}-et lent` : "";
    return {
      tone: "warn",
      text: `0 küldhető: ${listed(open)} országban senkit nem enged át az átadás, vagy nincs feliratkozott, ki nem zárt kontakt${tail}.`,
    };
  }
  if (closed.length > 0) {
    return {
      tone: "muted",
      text: `${listed(closed)} zárva: oda nem jut a számból, amíg be nem kapcsolod a ${SWITCH}-et lent.`,
    };
  }
  return null;
}

/** Why a started pool's budget today is 0, or null when it is not 0. The card
 * never shows a 0 without this. */
export function poolZeroReason(
  pool: WarmupPool,
  account: WarmupAccount | null,
  rows: CountryRow[],
): string | null {
  if (pool.budget_today > 0 || pool.warmup_state === "cold" || pool.is_paused) return null;
  if (pool.throttle_factor <= 0 && pool.full_budget_today > 0) {
    return "Az őrszem 0-ra lassította: semmi nem megy ki rajta, amíg el nem engedi.";
  }
  if (account === null) {
    return pool.daily_limit_per_ip <= 0
      ? "A pool napi limitje 0 / IP: semmi nem megy ki rajta."
      : "A mai keret nulla: semmi nem megy ki rajta.";
  }
  if (account.daily_limit <= 0) {
    return `A(z) ${account.code} SES-fiók napi száma 0: a fenti kártyán a „Napi szám” gombbal állítható.`;
  }
  if (pool.countries.length === 0) {
    return `Nincs aktív küldő-fiókja (ország), így nem jut rá a(z) ${account.code} fiók számából.`;
  }
  const share = account.split.find((entry) => entry.code === pool.code);
  const status = new Map(rows.map((row) => [row.country, row.status]));
  const closed = pool.countries.filter((country) => status.get(country) === "closed");
  if (!share || share.mailable <= 0) {
    if (closed.length === pool.countries.length) {
      return `0 küldhető: ${listed(closed)} nincs megnyitva (a Klaviyo küld, N = 0), így nem jut rá a(z) ${account.code} fiók számából — kapcsold be a ${SWITCH}-et fent.`;
    }
    return `0 küldhető ${listed(pool.countries)} országban (senkit nem enged át az átadás, vagy nincs feliratkozott, ki nem zárt kontakt), így nem jut rá a(z) ${account.code} fiók számából.`;
  }
  if (share.share > 0 && share.share < pool.ip_count) {
    return `A(z) ${account.code} fiók számából ${count(share.share)} jut rá, kevesebb, mint IP-nként 1 (${pool.ip_count} IP).`;
  }
  if (share.share <= 0) {
    return `A(z) ${account.code} fiók mai számát a többi pool már elküldte vagy megkapta: ma erre nem jut több.`;
  }
  return "A mai keret nulla: semmi nem megy ki rajta.";
}
